import axios from '../services/axiosConfig';

const API_URL = process.env.REACT_APP_API_URL;

// Helper to get userId from session storage
const getUserId = () => {
  try {
    const userStr = sessionStorage.getItem('user');
    if (userStr) {
      const userData = JSON.parse(userStr);
      return userData.userId || userData.UserID || userData.userID || userData.UserId || 1;
    }
  } catch (err) {
    console.error('Error getting user ID from session', err);
  }
  return 1; // Default user ID if not found
};

/**
 * Get all service configurations
 * @returns {Promise} Promise object representing the list of service configurations
 */
export const getAllServiceConfigs = async () => {
  try {
    const response = await axios.get(`${API_URL}/ServiceConfig/LoadServiceName`);
    return response.data.Services || [];
  } catch (error) {
    console.error('Error fetching service configs:', error);
    throw error;
  }
};

/**
 * Get a service configuration by ID
 * @param {number} id - The service configuration ID
 * @returns {Promise} Promise object representing the service configuration
 */
export const getServiceConfigById = async (id) => {
  try {
    const response = await axios.get(`${API_URL}/ServiceConfig/GetServiceDetails?serviceName=${id}`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching service config with ID ${id}:`, error);
    throw error;
  }
};

/**
 * Create a new service configuration
 * @param {Object} config - The service configuration data
 * @returns {Promise} Promise object representing the created service configuration
 */
export const createServiceConfig = async (config) => {
  try {
    const payload = {
      ServiceName: config.name,
      Url: config.url,
      Method: config.method,
      Request: config.request,
      UserID: getUserId()
    };
    const response = await axios.post(`${API_URL}/ServiceConfig/SaveService`, payload);
    return response.data;
  } catch (error) {
    console.error('Error creating service config:', error);
    throw error;
  }
};

/**
 * Update an existing service configuration
 * @param {number} id - The service configuration ID
 * @param {Object} config - The updated service configuration data
 * @returns {Promise} Promise object representing the updated service configuration
 */
export const updateServiceConfig = async (id, config) => {
  try {
    const payload = {
      serviceId: id.toString(),
      Url: config.url,
      Method: config.method,
      Request: config.request,
      IsActive: config.isActive ? '1' : '0',
      UserID: getUserId()
    };
    const response = await axios.put(`${API_URL}/ServiceConfig/EditService`, payload);
    return response.data;
  } catch (error) {
    console.error(`Error updating service config with ID ${id}:`, error);
    throw error;
  }
};

/**
 * Delete a service configuration
 * @param {number} id - The service configuration ID
 * @returns {Promise} Promise object representing the result
 */
export const deleteServiceConfig = async (id) => {
  try {
    // Note: There doesn't seem to be a delete endpoint in your controller
    // You might need to implement this in your backend first
    console.warn('Delete service config functionality is not implemented in the backend');
    return { success: false, message: 'Operation not supported' };
  } catch (error) {
    console.error(`Error deleting service config with ID ${id}:`, error);
    throw error;
  }
};

/**
 * Get all service parameter configurations
 * @returns {Promise} Promise object representing the list of parameter configurations
 */
export const getAllServiceParameterConfigs = async () => {
  try {
    const response = await axios.get(`${API_URL}/ServiceConfig/LoadParameterName`);
    return response.data.ServiceParameter || [];
  } catch (error) {
    console.error('Error fetching service parameter configs:', error);
    throw error;
  }
};

/**
 * Get a service parameter configuration by ID
 * @param {number} id - The parameter ID
 * @returns {Promise} Promise object representing the parameter configuration
 */
export const getServiceParameterById = async (id) => {
  try {
    const response = await axios.get(`${API_URL}/ServiceConfig/GetParameterDetails?parameterName=${id}`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching service parameter with ID ${id}:`, error);
    throw error;
  }
};

/**
 * Create a new service parameter
 * @param {Object} parameter - The service parameter data
 * @returns {Promise} Promise object representing the created parameter
 */
export const createServiceParameter = async (parameter) => {
  try {
    const payload = {
      ParameterName: parameter.name,
      KeySource: parameter.keySourcePath || '',
      ValueSource: parameter.valueSourcePath,
      Source: parameter.source,
      ServiceId: parameter.serviceID || '',
      RegEx: parameter.regex || '',
      ShowEmptyAsNull: parameter.handleNull || 'ASEmpty',
      UserID: getUserId()
    };
    const response = await axios.post(`${API_URL}/ServiceConfig/SaveParameter`, payload);
    return response.data;
  } catch (error) {
    console.error('Error creating service parameter:', error);
    throw error;
  }
};

/**
 * Update an existing service parameter
 * @param {number} id - The parameter ID
 * @param {Object} parameter - The updated parameter data
 * @returns {Promise} Promise object representing the updated parameter
 */
export const updateServiceParameter = async (id, parameter) => {
  try {
    const payload = {
      paramId: id.toString(),
      KeySource: parameter.keySourcePath || '',
      ValueSource: parameter.valueSourcePath,
      Source: parameter.source,
      ServiceId: parameter.serviceID || '',
      RegEx: parameter.regex || '',
      IsActive: parameter.isActive ? '1' : '0',
      ShowEmptyAsNull: parameter.handleNull || 'ASEmpty',
      UserID: getUserId()
    };
    const response = await axios.put(`${API_URL}/ServiceConfig/EditParameter`, payload);
    return response.data;
  } catch (error) {
    console.error(`Error updating service parameter with ID ${id}:`, error);
    throw error;
  }
};

/**
 * Delete a service parameter
 * @param {number} id - The parameter ID
 * @returns {Promise} Promise object representing the result
 */
export const deleteServiceParameter = async (id) => {
  try {
    // Note: There doesn't seem to be a delete endpoint in your controller
    // You might need to implement this in your backend first
    console.warn('Delete service parameter functionality is not implemented in the backend');
    return { success: false, message: 'Operation not supported' };
  } catch (error) {
    console.error(`Error deleting service parameter with ID ${id}:`, error);
    throw error;
  }
};

// Fetch parameter names from external API
export const fetchParameterNames = async () => {
  try {
    const response = await axios.get(`${API_URL}/ServiceConfig/LoadParameterName`);
    const parameterNames = response.data.parameters;

    // Ensure unique parameter names
    const uniqueParameters = Array.from(new Set(parameterNames.map((param) => param.name))).map((name) => {
      return parameterNames.find((param) => param.name === name);
    });

    return uniqueParameters;
  } catch (err) {
    console.error("Error loading parameter names:", err);
    throw err;
  }
};

/**
 * Load service names from the API
 * @returns {Promise<Array>} Array of service names
 */
export const fetchServiceNames = async () => {
  try {
    const response = await axios.get(`${API_URL}/ServiceConfig/LoadServiceName`);
    return response.data.services || response.data || [];
  } catch (err) {
    console.error("Error loading service names:", err);
    throw err;
  }
};
